import { Button, Chip, Grid, Paper, Typography, CircularProgress, Alert } from "@mui/material";
import styles from "../../Styles/clientPanel.module.css";
import genericStyles from "../../Styles/genericStyles.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ClientMessageModel } from "../../Utils/Models";
import moment from "moment";
import Config from "../../Utils/Config";
import { MessagesStatus, MessagesType, endpoints } from "../../Utils/UrlEnum";
import { ShoppingCart } from "@mui/icons-material";
import GenericModal from "../GenericModal";
import { EWidths } from "../../Utils/Enums";
import { useState } from "react";
import { postData } from "../../Services/postData";

interface ClientMessagesSectionProps {
  messages: ClientMessageModel[];
}

export default function ClientMessagesSection(props: ClientMessagesSectionProps) {
  const { messages } = props;
  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ClientMessageModel | null>(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  /**
   * Get the status label in Romanian based on status value
   */
  const getStatusLabel = (status: string | null) => {
    switch (status) {
      case MessagesStatus.notRead:
        return Vocabulary.unread;
      case MessagesStatus.read:
        return Vocabulary.read;
      case MessagesStatus.inProgress:
        return Vocabulary.in_progress;
      case MessagesStatus.completed:
        return Vocabulary.completed;
      case MessagesStatus.canceled:
        return Vocabulary.canceled;
      default:
        return status || "-";
    }
  };

  /**
   * Get the type label in Romanian based on type value
   */
  const getTypeLabel = (type: string | null) => {
    switch (type) {
      case MessagesType.custody:
        return Vocabulary.custodie;
      case MessagesType.service:
        return Vocabulary.service;
      case MessagesType.other:
        return Vocabulary.others;
      default:
        return type || "-";
    }
  };

  /**
   * Get color for status chip
   */
  const getStatusColor = (status: string | null): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case MessagesStatus.notRead:
        return "error";
      case MessagesStatus.read:
        return "info";
      case MessagesStatus.inProgress:
        return "warning";
      case MessagesStatus.completed:
        return "success";
      case MessagesStatus.canceled:
        return "default";
      default:
        return "default";
    }
  };

  /**
   * Toggle the order creation modal
   */
  const toggleOrderModal = () => {
    setOpenOrderModal(!openOrderModal);
    if (openOrderModal) {
      setSelectedMessage(null);
    }
  };

  /**
   * Open the order creation modal for a specific message
   * This will be implemented in the future with proper integration to second application
   */
  const handleCreateOrder = (message: ClientMessageModel) => {
    setSelectedMessage(message);
    setOrderError(null);
    setOpenOrderModal(true);
  };

  /**
   * Creates an order in the client-ERP system
   * The order will be created with the data from the message
   */
  const confirmCreateOrder = async () => {
    if (!selectedMessage) return;

    setIsCreatingOrder(true);
    setOrderError(null);

    try {
      // Get current user ID from localStorage or context
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userId = user?._id || user?.id;

      // Create ERP order payload matching the client-erp structure
      const erpOrderData = {
        orderNumber: 0,
        type: "ESTIMATE",
        isClosed: false,
        isFinished: false,
        priority: "NORMAL",
        notes: [
          {
            _id: null,
            type: "ISSUE",
            title: "",
            text: selectedMessage.message?.join('\n') || '',
            createdBy: userId,
            attachments: [],
            emailDetails: null
          }
        ],
        orderedBy: selectedMessage.clientId, // Will be filled by backend
        handledBy: userId,
        oldStatus: null,
        status: null,
        location: null,
        shipments: [],
        lines: [
          {
            _id: null,
            product: null,
            inventory: null,
            variant: null,
            quantity: 0,
            price: 0,
            vat: 0,
            discount: 0,
            totalWithoutVat: 0,
            total: 0
          }
        ],
        workflow: null,
        workflowSteps: [],
        device: null,
        total: "0.00",
        totalWithoutVat: 0,
        totalCharged: 0,
        totalRemaining: "0.00"
      };

      await postData(
        `${endpoints.messages.main}/${selectedMessage._id}/create-order`,
        erpOrderData
      );


      // Close modal on success
      setOpenOrderModal(false);
      setSelectedMessage(null);

      // Optional: Show success notification or refresh messages
      // You might want to use a notification context here
    } catch (error: any) {
      console.error(Vocabulary.errorCreatingErpOrder, error);
      setOrderError(
        error?.response?.data?.message ||
        Vocabulary.errorCreatingErpOrder
      );
    } finally {
      setIsCreatingOrder(false);
    }
  };

  /**
   * Render a single message card
   */
  const renderMessage = (message: ClientMessageModel) => {
    return (
      <Paper
        key={message._id}
        style={{
          padding: 16,
          marginBottom: 16,
          borderLeft: "4px solid #352618"
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.name}:
            </Typography>
            <Typography variant="body1">
              {message.name || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.email}:
            </Typography>
            <Typography variant="body1">
              {message.email || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.phone}:
            </Typography>
            <Typography variant="body1">
              {message.phone || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.type}:
            </Typography>
            <Chip
              label={getTypeLabel(message.type)}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.status}:
            </Typography>
            <Chip
              label={getStatusLabel(message.status)}
              size="small"
              color={getStatusColor(message.status)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.message}:
            </Typography>
            {message.message && message.message.length > 0 ? (
              message.message.map((msg, index) => (
                <Typography key={index} variant="body1" style={{ marginTop: 4 }}>
                  {msg}
                </Typography>
              ))
            ) : (
              <Typography variant="body1">-</Typography>
            )}
          </Grid>

          {message.files && message.files.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {Vocabulary.attachedFiles}: {message.files.length}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">
              {Vocabulary.creationDate}:
            </Typography>
            <Typography variant="body1">
              {message.createdAt
                ? moment(message.createdAt).format(Config.momentEUDateFormat)
                : "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} style={{ textAlign: "right" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShoppingCart />}
              onClick={() => handleCreateOrder(message)}
            >
              {Vocabulary.createOrder}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <>
      <Paper className={styles.paper}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              {Vocabulary.clientMessages} ({messages.length})
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {messages.length > 0 ? (
              messages.map((message) => renderMessage(message))
            ) : (
              <Typography variant="body1" color="textSecondary">
                {Vocabulary.noMessages}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <GenericModal
        onClose={toggleOrderModal}
        maxWidth={EWidths.SM}
        open={openOrderModal}
        title={Vocabulary.createOrder}
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              color="secondary"
              variant="outlined"
              fullWidth
              onClick={toggleOrderModal}
              disabled={isCreatingOrder}
            >
              {Vocabulary.close}
            </Button>
            <Button
              color="primary"
              variant="contained"
              fullWidth
              onClick={confirmCreateOrder}
              disabled={isCreatingOrder || !selectedMessage?.clientId}
              startIcon={isCreatingOrder ? <CircularProgress size={20} /> : <ShoppingCart />}
            >
              {isCreatingOrder ? Vocabulary.creating : Vocabulary.createOrder}
            </Button>
          </div>
        }
      >
        <Grid container spacing={2} style={{ padding: 20 }}>
          {orderError && (
            <Grid item xs={12}>
              <Alert severity="error">{orderError}</Alert>
            </Grid>
          )}

          {selectedMessage && !selectedMessage.clientId && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {Vocabulary.messageNotLinkedToClient || "This message is not linked to a client. Please link it to a client first."}
              </Alert>
            </Grid>
          )}

          {selectedMessage && (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {Vocabulary.name}:
                </Typography>
                <Typography variant="body1">
                  {selectedMessage.name}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {Vocabulary.email}:
                </Typography>
                <Typography variant="body1">
                  {selectedMessage.email}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {Vocabulary.phone}:
                </Typography>
                <Typography variant="body1">
                  {selectedMessage.phone}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {Vocabulary.messageType}:
                </Typography>
                <Typography variant="body1">
                  {getTypeLabel(selectedMessage.type)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {Vocabulary.message}:
                </Typography>
                {selectedMessage.message && selectedMessage.message.length > 0 ? (
                  selectedMessage.message.map((msg, index) => (
                    <Typography key={index} variant="body1" style={{ marginTop: 4 }}>
                      {msg}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body1">-</Typography>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </GenericModal>
    </>
  );
}
